import type {
    ChargingActivityExportParams,
    ChargingActivityParams,
    ClusteredChargingSummaryParams,
} from '@/api/chargingActivity/types';
import {
    ChargingActivityClusteredSummary,
    type ChargingActivityClusteredSummaryType,
    ChargingSessions,
    type ChargingSessionsType,
} from '@/api/schema';
import { downloadFile } from '@/features/chargingActivity/utils/download';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { axiosInstance } from '@/lib/api/axios';
import { useAccountStore } from '@/stores/accountStore';

const fetchChargingActivity = async (params: { filters: ChargingActivityParams }): Promise<ChargingSessionsType> => {
    const { data } = await axiosInstance.post<ChargingSessionsType>('/charging-activity', params);

    return data;
};

const fetchClusteredChargingSummary = async (
    params: ClusteredChargingSummaryParams
): Promise<ChargingActivityClusteredSummaryType> => {
    const { data } = await axiosInstance.get<ChargingActivityClusteredSummaryType>(
        '/charging-activity/clustered-summary',
        { params }
    );

    return data;
};

const downloadChargingActivity = async (params: ChargingActivityExportParams): Promise<void> => {
    const contentType =
        params.export.format === 'XLSX'
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'text/csv';

    const from = {
        start: params.filters.dateRange.localStartDate.split('T')[0],
        to: params.filters.dateRange.localEndDate.split('T')[0],
    };

    const defaultFilename = `MAN_ChargeGo_Charging_Activity_${from.start}_${from.to}.${params.export.format.toLowerCase()}`;

    const response = await axiosInstance.post('/charging-activity', params, {
        responseType: 'blob',
        headers: {
            Accept: contentType,
        },
    });

    await downloadFile(response, defaultFilename);
};

export const useChargingActivityDownload = () =>
    useApiMutation<void, ChargingActivityExportParams>({
        mutationFn: downloadChargingActivity,
    });

export const useChargingActivity = (params: { filters: ChargingActivityParams }) => {
    const { userProfile } = useAccountStore();

    return useApiQuery({
        queryKey: ['charging-activity', params],
        fetcher: () => fetchChargingActivity(params),
        validator: ChargingSessions,
        queryOptions: {
            enabled: !!userProfile?.account,
            staleTime: 5 * 60 * 1000,
            gcTime: 30 * 60 * 1000,
        },
    });
};

export const useClusteredChargingSummary = (params: ClusteredChargingSummaryParams) => {
    const { userProfile } = useAccountStore();

    return useApiQuery({
        queryKey: ['clustered-charging-summary', params],
        fetcher: () => fetchClusteredChargingSummary(params),
        validator: ChargingActivityClusteredSummary,
        queryOptions: {
            enabled: !!userProfile?.account,
            staleTime: 5 * 60 * 1000,
            gcTime: 30 * 60 * 1000,
        },
    });
};
